import React, { useState } from "react";
import { Megaphone, Download, FileText, Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useList } from "@/hooks/useApi";
import { useQueryClient } from "@tanstack/react-query";
import { Loading, EmptyState, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export const UPDATE_CATEGORIES = ["New Product", "New Fabric Development", "Innovation Lab", "New Printing Technique", "Sampling Update", "Production Update", "Quality Update", "Dispatch Update", "Important Announcement", "Urgent Buyer Update"];
export const UPDATE_FILTERS = ["all", "unread", "New Product", "New Fabric Development", "Innovation Lab", "Production Update", "Sampling Update", "Important", "Urgent"];

export function PriorityBadge({ priority }) {
  const map = { Urgent: "bg-destructive/15 text-destructive", Important: "bg-warning/15 text-warning", Normal: "bg-secondary text-muted-foreground" };
  return <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full", map[priority] || map.Normal)}>{priority || "Normal"}</span>;
}

function markRead(qc, u) {
  if (!u.is_read) api.post(`/my-updates/${u.id}/read`).then(() => { qc.invalidateQueries({ queryKey: ["my-updates"] }); qc.invalidateQueries({ queryKey: ["my-updates-unread"] }); }).catch(() => {});
}

export function UpdateDetail({ u, onClose }) {
  const qc = useQueryClient();
  const { data: products = [] } = useList("products", "/products");
  const prod = u.related_product_id ? products.find((p) => p.id === u.related_product_id) : null;
  const imgs = u.images || [];
  const [big, setBig] = useState(imgs[0]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="update-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">{u.title} <PriorityBadge priority={u.priority} /></DialogTitle>
          <DialogDescription>{u.category} · {(u.published_at || u.created_at) ? new Date(u.published_at || u.created_at).toLocaleString() : ""}{u.created_by ? ` · by ${u.created_by}` : ""}</DialogDescription>
        </DialogHeader>
        {imgs.length > 0 && (
          <>
            <div className="aspect-video bg-secondary rounded-lg overflow-hidden flex items-center justify-center">{big && <img src={big} alt="" className="max-h-full max-w-full object-contain" />}</div>
            {imgs.length > 1 && <div className="flex gap-2 overflow-x-auto pb-1">{imgs.map((im, i) => <img key={i} src={im} alt="" onClick={() => setBig(im)} className={cn("w-16 h-16 rounded object-cover cursor-pointer shrink-0", big === im && "ring-2 ring-primary")} />)}</div>}
            <div className="flex flex-wrap gap-2">{imgs.map((im, i) => <a key={i} href={im} target="_blank" rel="noreferrer" download className="text-xs inline-flex items-center gap-1 bg-secondary px-2 py-1 rounded"><Download className="w-3 h-3" /> Image {i + 1}</a>)}</div>
          </>
        )}
        {u.description && <p className="text-sm whitespace-pre-wrap">{u.description}</p>}
        {(u.files || []).length > 0 && <div className="flex flex-wrap gap-2 border-t border-border pt-3">{u.files.map((f, i) => <a key={i} href={f.url} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 bg-secondary px-2 py-1 rounded" data-testid="update-file"><FileText className="w-3 h-3" /> {f.name || "file"}</a>)}</div>}
        {prod && (
          <div className="border-t border-border pt-3 flex items-center gap-3" data-testid="update-related-product">
            {prod.image_url ? <img src={prod.image_url} alt="" className="w-14 h-14 rounded object-cover" /> : <div className="w-14 h-14 rounded bg-secondary" />}
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Related product</div><div className="font-medium text-sm">{prod.name}</div><div className="text-xs text-muted-foreground">{prod.sku} · {[prod.fabric, prod.gsm].filter(Boolean).join(" · ")}</div></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UpdateCard({ u, onOpen }) {
  const thumb = (u.images || [])[0];
  return (
    <Card className={cn("p-3 flex gap-3 cursor-pointer items-start", !u.is_read && "border-primary/40 bg-primary/5")} onClick={onOpen} data-testid={`update-card-${u.id}`}>
      {thumb ? <img src={thumb} alt="" className="w-16 h-16 rounded object-cover shrink-0" /> : <div className="w-16 h-16 rounded bg-secondary shrink-0 flex items-center justify-center"><Megaphone className="w-5 h-5 text-muted-foreground" /></div>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">{!u.is_read && <span className="w-2 h-2 rounded-full bg-primary" data-testid="update-unread-dot" />}<span className="font-semibold text-sm truncate">{u.title}</span><PriorityBadge priority={u.priority} /></div>
        <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{u.description}</div>
        <div className="flex items-center justify-between mt-1.5"><span className="text-[10px] uppercase tracking-wider text-primary">{u.category}</span><span className="text-[10px] text-muted-foreground">{(u.published_at || u.created_at) ? new Date(u.published_at || u.created_at).toLocaleDateString() : ""}</span></div>
      </div>
      <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid={`view-update-${u.id}`}>View Update</Button>
    </Card>
  );
}

export function UpdatesFeed() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const { data: updates, isLoading } = useList("my-updates", "/my-updates", { filter });
  const [detail, setDetail] = useState(null);
  const open = (u) => { setDetail(u); markRead(qc, u); };
  const markAll = () => api.post("/my-updates/read-all").then(() => { qc.invalidateQueries({ queryKey: ["my-updates"] }); qc.invalidateQueries({ queryKey: ["my-updates-unread"] }); }).catch(() => {});
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-56 h-9" data-testid="updates-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{UPDATE_FILTERS.map((f) => <SelectItem key={f} value={f}>{f === "all" ? "All Updates" : f === "unread" ? "Unread" : f}</SelectItem>)}</SelectContent></Select>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={markAll} data-testid="updates-mark-all"><CheckCheck className="w-4 h-4" /> Mark all as read</Button>
      </div>
      {isLoading ? <Loading /> : !updates?.length ? <Card className="p-8"><EmptyState title="No updates" hint="Updates from the manufacturing team will appear here." testid="updates-empty" /></Card> : (
        <div className="space-y-2">{updates.map((u) => <UpdateCard key={u.id} u={u} onOpen={() => open(u)} />)}</div>
      )}
      {detail && <UpdateDetail u={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
